/**
 * Dispersão força × momento (proto:968-974). Quatro quadrantes prescritivos:
 * cada um diz o que fazer, não apenas onde o ponto caiu.
 *
 * IMPORTANTE — honestidade de dado: o protótipo calcula os dois eixos a
 * partir de `ELEG_BAIRRO[2024]` e `ELEG_BAIRRO[2020]` (votos do mandato por
 * bairro, em dois ciclos). Essa série não existe nos dados reais da
 * aplicação hoje:
 *   - `eleg.resultado_zona`/`eleg.detalhe_zona` guardam a apuração do TSE por
 *     ZONA ELEITORAL, não por bairro/território, e não há tabela de
 *     mapeamento zona↔bairro em nenhum lugar do schema.
 *   - `terr.bairro` tem `votos`/`percentual`, mas como um valor único sem
 *     coluna de ciclo — não há uma segunda medição no tempo para comparar.
 * Por isso este componente recebe `pontos` já prontos (para o dia em que
 * essa série existir) e, na ausência deles, declara a indisponibilidade em
 * vez de desenhar uma dispersão vazia ou com números fabricados.
 */
export interface PontoMatriz {
  id: string;
  nome: string;
  /** Força histórica (0-100). */
  forca: number;
  /** Momento atual (0-100). */
  momento: number;
}

const QUADRANTES = [
  {
    nome: 'Consolidar',
    texto: 'Entregar e comunicar; réguas de ação; proteger lideranças.',
  },
  {
    nome: 'Crescer',
    texto: 'Ampliar contatos e lideranças; visitas quinzenais; ações visíveis.',
  },
  {
    nome: 'Defender',
    texto: 'Investigar queda; pesquisa de 1 pergunta; reaproximar lideranças.',
  },
  {
    nome: 'Explorar',
    texto: 'Mapear lideranças; presença mensal; ações de baixo custo e alta visibilidade.',
  },
] as const;

// Escala única: define a área do gráfico (60-600 em x, 20-360 em y) e é usada
// por marcas, fundos dos quadrantes, eixos e rótulos — nenhum deles calcula
// posição por conta própria.
const X0 = 60;
const X1 = 600;
const Y0 = 20;
const Y1 = 360;
const escalaX = (forca: number) => X0 + (Math.max(0, Math.min(100, forca)) / 100) * (X1 - X0);
// Y cresce para baixo em SVG: momento maior fica mais acima (y menor).
const escalaY = (momento: number) => Y1 - (Math.max(0, Math.min(100, momento)) / 100) * (Y1 - Y0);

function quadranteDe(p: PontoMatriz): (typeof QUADRANTES)[number]['nome'] {
  if (p.forca >= 40 && p.momento >= 50) return 'Consolidar';
  if (p.forca < 40 && p.momento >= 50) return 'Crescer';
  if (p.forca >= 40) return 'Defender';
  return 'Explorar';
}

export function MatrizForcaMomento({ pontos }: { pontos: PontoMatriz[] }) {
  const contagemPorQuadrante = new Map<string, PontoMatriz[]>(
    QUADRANTES.map((q) => [q.nome, [] as PontoMatriz[]]),
  );
  pontos.forEach((p) => contagemPorQuadrante.get(quadranteDe(p))?.push(p));

  return (
    <div className="grid g21">
      <div className="card">
        <h2>Força histórica × momento</h2>
        {pontos.length === 0 ? (
          <div style={{ padding: '24px 8px', color: 'var(--ink3)', fontSize: 13, lineHeight: 1.5 }}>
            Esta dispersão não está disponível: faltam os votos do mandato por
            território em dois ciclos eleitorais (ex.: 2020 e 2024). A
            apuração do TSE hoje é registrada por zona eleitoral, e não existe
            um mapeamento entre zona eleitoral e território que permita
            derivar essa série por bairro.
          </div>
        ) : (
          <svg viewBox="0 0 640 400" style={{ width: '100%', height: 400 }}>
            <rect x={X0} y={Y0} width={(X1 - X0) / 2} height={(Y1 - Y0) / 2} fill="var(--warn-soft)" opacity={0.5} />
            <rect
              x={X0 + (X1 - X0) / 2}
              y={Y0}
              width={(X1 - X0) / 2}
              height={(Y1 - Y0) / 2}
              fill="var(--good-soft)"
              opacity={0.5}
            />
            <rect
              x={X0}
              y={Y0 + (Y1 - Y0) / 2}
              width={(X1 - X0) / 2}
              height={(Y1 - Y0) / 2}
              fill="var(--crit-soft)"
              opacity={0.5}
            />
            <rect
              x={X0 + (X1 - X0) / 2}
              y={Y0 + (Y1 - Y0) / 2}
              width={(X1 - X0) / 2}
              height={(Y1 - Y0) / 2}
              fill="var(--accent-soft)"
              opacity={0.5}
            />
            <g fontFamily="Manrope" fontSize={11}>
              <text x={X0 + 10} y={Y0 + 16} fill="var(--ink3)">CRESCER · força baixa, momento bom</text>
              <text x={X0 + (X1 - X0) / 2 + 10} y={Y0 + 16} fill="var(--ink3)">CONSOLIDAR · forte e subindo</text>
              <text x={X0 + 10} y={Y1 - 8} fill="var(--ink3)">EXPLORAR · fraco e parado</text>
              <text x={X0 + (X1 - X0) / 2 + 10} y={Y1 - 8} fill="var(--ink3)">DEFENDER · forte, momento fraco</text>
              <text x={(X0 + X1) / 2} y={Y1 + 25} textAnchor="middle" fill="var(--ink3)">Força histórica →</text>
              <text
                x={20}
                y={(Y0 + Y1) / 2}
                transform={`rotate(-90 20 ${(Y0 + Y1) / 2})`}
                textAnchor="middle"
                fill="var(--ink3)"
              >
                Momento →
              </text>
            </g>
            {pontos.map((p) => {
              const x = escalaX(p.forca);
              const y = escalaY(p.momento);
              const r = 8;
              return (
                <g key={p.id}>
                  <circle cx={x} cy={y} r={r} fill="var(--accent)" opacity={0.85} stroke="var(--surface)" strokeWidth={2} />
                  <text
                    x={x}
                    y={y - r - 4}
                    textAnchor="middle"
                    fontFamily="Manrope"
                    fontSize={11}
                    fontWeight={700}
                    fill="var(--ink)"
                  >
                    {p.nome}
                  </text>
                </g>
              );
            })}
          </svg>
        )}
      </div>
      <div className="card">
        <h2>Recomendação por quadrante</h2>
        {QUADRANTES.map((q) => {
          const l = contagemPorQuadrante.get(q.nome) ?? [];
          return (
            <div key={q.nome} style={{ padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
              <b>{q.nome}</b> <span className="chip neutral">{l.length}</span>
              <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 2 }}>
                {l.length ? l.map((p) => p.nome).join(' · ') : '—'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>{q.texto}</div>
            </div>
          );
        })}
        {/*
          proto:971 define o gráfico como "tamanho = capilaridade (contatos);
          cor = presença territorial", e esta legenda repetia a definição como
          se ela estivesse implementada. Não está: o círculo usa raio fixo
          (`r = 8`) e cor fixa (`var(--accent)`) — `PontoMatriz` não tem campo
          de contatos nem de presença. Anunciar codificações que o SVG não faz é
          pior do que não ter legenda: quem lê passa a interpretar tamanho e cor
          que não querem dizer nada.
        */}
        <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8 }}>
          Tamanho e cor dos pontos são fixos e não codificam nada: só as posições informam.
        </p>
      </div>
    </div>
  );
}
